<template>
  <div class="checkout-extrasell-product vg-confirmation-extrasell">
    <template v-if="showRetry">
      <div class="retry-payment-form" v-if="showpaymentform">
        <PaymentForm
          :paymentMethodsOptions="{
            sepa: { cssClass: 'field-icon fa-solid fa-building-columns' },
            bank_card: { cssClass: 'field-icon fa-solid fa-credit-card' },
            iframeStyle: 'orderPage',
          }"
          :orderType="stepType === 'postUpsells' ? 'upsell' : 'crosssell'"
        />
      </div>
      <button
        type="button"
        class="skip-process-btn"
        @click.prevent="(event) => skipProcessAction(event)"
      >
        {{ $t("labels.skipProcess") }}
      </button>
    </template>
    <template v-else-if="isOfferExpired && hasExtrasellOffers">
      <header class="order-summary__header vg-confirmation-extrasell__header">
        <span class="order-summary__header-icon" aria-hidden="true">
          <svg viewBox="0 0 576 512" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3L288.1 439.8l128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"
            />
          </svg>
        </span>
        <span class="order-summary__header-label">{{
          $t("labels.confirmationExtrasellTitle")
        }}</span>
      </header>
      <p class="vg-confirmation-extrasell__expired" role="status">
        {{ $t("messages.confirmationExtrasellExpired") }}
      </p>
    </template>
    <template v-else-if="isLoadingExtrasell || products">
      <header
        v-if="products"
        class="order-summary__header vg-confirmation-extrasell__header"
      >
        <span class="order-summary__header-icon" aria-hidden="true">
          <svg viewBox="0 0 576 512" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3L288.1 439.8l128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"
            />
          </svg>
        </span>
        <span class="order-summary__header-label">{{
          $t("labels.confirmationExtrasellTitle")
        }}</span>
      </header>

      <div
        v-if="isLoadingExtrasell"
        class="vg-confirmation-extrasell__loader"
        role="status"
        aria-live="polite"
      >
        <span
          class="vg-confirmation-extrasell__loader-spinner"
          aria-hidden="true"
        ></span>
        <span class="vg-confirmation-extrasell__loader-text">{{
          $t("labels.confirmationExtrasellLoading")
        }}</span>
      </div>

      <ProductExtraProducts
        v-else-if="products"
        :postMountedProducts="products"
        :showaddtocart="showaddtocart"
        :showprocessorder="showprocessorder"
        :step="step"
        :showBeforeText="true"
        :showAfterText="true"
        :orderType="stepType === 'postUpsells' ? 'upsell' : 'crosssell'"
      />
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { useOrderHistoryStore } from "@/store/orderHistory";
import ProductExtraProducts from "@/components/product/ExtraProducts.vue";
import AbTests from "@/services/abTests";
import CartService from "@/services/cart";
import Redirect from "@/services/redirect";
import Utils from "@/services/utils";
import { useDraftStore } from "@/store/draft";
import Orion from "@/services/orion";
import { useCartStore } from "@/store/cart";
import { constants } from "@/constants";
import PaymentForm from "@/components/checkout/PaymentForm.vue";
import { useTrackingStore } from "@/store/tracking";
import PaymentMonitoring, {
  FLOW_STEPS,
  BUG_IDS,
} from "@/services/paymentMonitoring";
import { setPostPurchaseTitle } from "@/services/browserTitle";
import {
  ensureConfirmationOfferWindowStart,
  getConfirmationOfferWindowRemainingMs,
  isConfirmationOfferWindowExpired,
} from "@/services/confirmationOfferWindow";
import {
  fetchExtrasellWithStepAdvance,
  postUpsellAppliesToPayload,
} from "@/services/extrasellFunnel";
import {
  buildExtrasellPayload,
  clearPreviewExtrasellConsumed,
  isPreviewExtrasellConsumed,
  isStalePostUpsellPayload,
  markPreviewExtrasellConsumed,
  resolveUpsellParentChoiceIds,
} from "@/services/extrasellPayload";
import {
  buildCrossSellPreviewRequest,
  buildPreviewHistoryCart,
  buildPreviewOrder,
  buildUpsellPreviewRequest,
  electPreviewInitialChoices,
  hasVanguardOnlyPreviewTab,
  isVanguardOnlyExtrasellSession,
  markVanguardOnlyPreviewTab,
  resolvePreviewHandoffHistoryItem,
  shouldInitializePreview,
  scopePreviewOrderHistory,
} from "@/services/confirmationPreview";
import { VANGUARD_ONLY_CONTACT } from "@/services/testFixtures";
import { normalizePostUpsellChoiceGroup } from "@/services/postUpsellGroups";
import { useConfirmationContextStore } from "@/store/confirmationContext";

export default {
  name: "CheckoutExtrasell",
  components: {
    ProductExtraProducts,
    PaymentForm,
  },
  data() {
    return {
      step: 0,
      stepType: null,
      products: null,
      showprocessorder: false,
      showaddtocart: false,
      showRetry: false,
      showpaymentform: false,
      previewDone: false,
      isInitializingPreview: false,
      retryOrderId: null,
      retryItem: null,
      newRetryOrderId: null,
      newRetryOrder: null,
      processingOrder: false,
      onVueAppMounted: null,
      previewHistoryItem: null,
      isLoadingExtrasell: false,
      isOfferExpired: false,
      hasExtrasellOffers: false,
      offerWindowExpiryTimer: null,
      confirmationFlowPromise: null,
      callLambdaPromise: null,
      callLambdaFingerprint: null,
      lastAppliedFingerprint: null,
    };
  },
  computed: {
    historyLastUpdate() {
      return useOrderHistoryStore().lastUpdate;
    },
    selectedPaymentMethod() {
      return useDraftStore().selectedPaymentMethod;
    },
    cartItemsCount() {
      return useCartStore().cart.items.length;
    },
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
  },
  async mounted() {
    this.onVueAppMounted = async () => {
      if (!this.retryOrderId && !this.previewDone) {
        await this.initializeConfirmationFlow();
      }
    };
    document.addEventListener("vue-app-mounted", this.onVueAppMounted);

    const queryStringData = Object.fromEntries(
      new URL(window.location).searchParams.entries(),
    );
    this.retryOrderId = queryStringData.retry;

    if (this.retryOrderId) {
      this.retryItem = useOrderHistoryStore().currentPageOrderHistory.find(
        (item) => item.order.externalOrderId === this.retryOrderId,
      );
      if (!this.retryItem || !this.retryItem.order) {
        const orderHistory = useOrderHistoryStore().currentPageOrderHistory;
        PaymentMonitoring.captureMessage("extrasell_retry_item_not_found", {
          level: "error",
          tags: {
            step: FLOW_STEPS.RETRY_CIT,
            reason: "retry_order_id_missing_from_history",
          },
          context: {
            retryOrderId: this.retryOrderId,
            orderHistorySize: orderHistory.length,
            orderHistoryIds: orderHistory.map((h) => h.order?.externalOrderId),
            explanation:
              "URL ?retry=X references an orderId not found in localStorage orderHistory. Possible causes: history cleared, different browser tab, or orderId mismatch after failed CIT retry.",
          },
          fingerprint: ["extrasell-retry-item-not-found"],
        });
        Redirect.redirectToError({ orderId: this.retryOrderId });
        return;
      }

      // Set stepType from retryItem for correct orderType determination (BUILD-1680)
      this.stepType =
        this.retryItem.order.orderType === "crosssell"
          ? "crossSells"
          : "postUpsells";
      if (this.retryItem.order.orderType === "crosssell") {
        this.step = this.retryItem.step;
      }

      if (this.retryItem) {
        // loop over previous cart items to add them to the new cart
        await this.buildCart();

        this.buildNewRetryOrder();

        useDraftStore().selectPaymentMethod(this.retryItem.order.paymentType);

        // send new order based on previous one
        //await this.sendRetryDraftOrder(); // not needed : selectPaymentMethod will do it

        useDraftStore().formValid(true);

        this.showRetry = true;
        this.showpaymentform = true;
        this.syncExtrasellPanel();
      }
    } else {
      this.isLoadingExtrasell = true;
      this.syncExtrasellPanel();
      await this.initializeConfirmationFlow();
    }

    this.syncExtrasellPanel();
  },
  watch: {
    historyLastUpdate: {
      handler() {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        // Preview still advances after skip once orderHistory is seeded.
        // Skip only while initializePreview is writing the synthetic entry.
        if (this.retryOrderId || this.isInitializingPreview) {
          return;
        }

        this.callLambda();
      },
    },
    selectedPaymentMethod: {
      handler(newValue, oldValue) {
        if (this.retryOrderId) {
          this.sendRetryDraftOrder().catch((error) => {
            PaymentMonitoring.captureException(error, {
              tags: {
                step: FLOW_STEPS.RETRY_CIT,
                payment_type: newValue || "unknown",
                reason: "send_retry_draft_order_failed",
              },
              context: {
                retryOrderId: this.retryOrderId,
                newRetryOrderId: this.newRetryOrder?.externalOrderId || null,
                previousPaymentMethod: oldValue || null,
                newPaymentMethod: newValue || null,
                explanation:
                  "sendRetryDraftOrder() threw after selectedPaymentMethod watcher fired. Orion.sendOrder() likely failed.",
              },
              fingerprint: ["extrasell-send-retry-draft-order-failure"],
            });
          });
        }
      },
    },
    lastCartUpdate: {
      handler() {
        // Remove auto-processing to allow users to add multiple post-upsells manually
        // Users will need to click the "Process Order" button to complete their upsell order
      },
    },
    isLoadingExtrasell() {
      this.syncExtrasellPanel();
    },
    products() {
      this.syncExtrasellPanel();
    },
    showRetry() {
      this.syncExtrasellPanel();
    },
  },
  methods: {
    clearOfferWindowExpiryTimer() {
      if (this.offerWindowExpiryTimer) {
        clearTimeout(this.offerWindowExpiryTimer);
        this.offerWindowExpiryTimer = null;
      }
    },
    updateOfferWindowExpiryState(orderId) {
      if (!orderId || !this.hasExtrasellOffers) {
        this.isOfferExpired = false;
        this.clearOfferWindowExpiryTimer();
        return false;
      }

      ensureConfirmationOfferWindowStart(orderId);
      this.isOfferExpired = isConfirmationOfferWindowExpired(orderId);
      this.clearOfferWindowExpiryTimer();

      if (!this.isOfferExpired) {
        const remainingMs = getConfirmationOfferWindowRemainingMs(orderId);
        if (remainingMs > 0) {
          this.offerWindowExpiryTimer = setTimeout(() => {
            if (!this.hasExtrasellOffers) {
              return;
            }
            this.isOfferExpired = true;
            this.isLoadingExtrasell = false;
            this.products = null;
            this.syncExtrasellPanel();
            this.offerWindowExpiryTimer = null;
          }, remainingMs + 50);
        }
      }

      return this.isOfferExpired;
    },
    syncExtrasellPanel() {
      this.$nextTick(() => {
        const panel = this.$el?.closest?.(".vg-confirmation-panel--extrasell");
        if (!panel) {
          return;
        }

        const visible =
          this.showRetry ||
          !!this.products ||
          (this.isOfferExpired && this.hasExtrasellOffers);
        panel.classList.toggle(
          "vg-confirmation-panel--extrasell-hidden",
          !visible,
        );
        panel.toggleAttribute("aria-hidden", !visible);
      });
    },
    getLastHistoryItem() {
      if (this.isPreviewMode()) {
        const scoped = this.getPreviewScopedHistory();
        if (scoped.length > 0) {
          return scoped[scoped.length - 1];
        }
      }
      return (
        useOrderHistoryStore().lastOrderHistoryItem || this.previewHistoryItem
      );
    },
    isPreviewMode() {
      return isVanguardOnlyExtrasellSession();
    },
    getPreviewScopedHistory() {
      const all = useOrderHistoryStore().orderHistory || [];
      return scopePreviewOrderHistory(all, {
        handoffItem: resolvePreviewHandoffHistoryItem(
          all,
          Redirect.getParamPlusData()?.orderId || null,
        ),
        previewHistoryItem: this.previewHistoryItem,
      });
    },
    seedPreviewOrderHistory(electedInitialChoices, productId, paymentType) {
      const cart = buildPreviewHistoryCart(
        window.productDetails,
        productId,
        electedInitialChoices,
      );
      const order = buildPreviewOrder({
        externalOrderId: Utils.generateUID(),
        paymentType: paymentType || "sepa",
        billingEmail: VANGUARD_ONLY_CONTACT.billingEmail,
      });

      this.previewHistoryItem = { cart, order, step: 0, action: "order" };
      clearPreviewExtrasellConsumed(window?.globalConfig?.domainId);
      useOrderHistoryStore().addOrderToHistory(cart, order, 0);
      return this.previewHistoryItem;
    },
    async initializeConfirmationFlow() {
      // Idempotent init: mounted() and the vue-app-mounted listener can both
      // request a run before the first one settles. They must share the
      // same run instead of each triggering their own callLambda.
      if (this.confirmationFlowPromise) {
        return this.confirmationFlowPromise;
      }

      this.confirmationFlowPromise = this.runConfirmationFlow().finally(() => {
        this.confirmationFlowPromise = null;
      });

      return this.confirmationFlowPromise;
    },
    async runConfirmationFlow() {
      if (window?.globalConfig?.isOrderPage && !this.isPreviewMode()) {
        useOrderHistoryStore().ensureCurrentOrderScope();
      }

      if (this.isPreviewMode()) {
        if (!this.previewDone) {
          this.previewDone = true;
          const all = useOrderHistoryStore().orderHistory || [];
          const handoff = resolvePreviewHandoffHistoryItem(
            all,
            Redirect.getParamPlusData()?.orderId || null,
          );
          const scoped = scopePreviewOrderHistory(all, {
            handoffItem: handoff,
            previewHistoryItem: this.previewHistoryItem,
          });
          const params = new URLSearchParams(window.location.search || "");
          if (
            !shouldInitializePreview(handoff, scoped, {
              isOpmTargetedPreview:
                params.has("step") || params.has("parentChoiceId"),
              isColdPreviewTab: !hasVanguardOnlyPreviewTab(),
            })
          ) {
            // Same-tab fake-pay: extrasell chain already seeded. Never
            // re-seed an initial order or the upsell loops.
            const resumeItem = handoff || scoped[scoped.length - 1];
            const orderId = resumeItem?.order?.externalOrderId;
            this.previewHistoryItem = resumeItem;
            ensureConfirmationOfferWindowStart(orderId);
            await this.callLambda();
            this.updateOfferWindowExpiryState(orderId);
            const previousPaymentType = resumeItem?.order?.paymentType;
            if (previousPaymentType) {
              useDraftStore().selectPaymentMethod(previousPaymentType);
            }
          } else {
            await this.initializePreview();
          }
        }
        return;
      }

      const historyItem = this.getLastHistoryItem();
      if (!historyItem) {
        this.isLoadingExtrasell = false;
        this.syncExtrasellPanel();
        return;
      }

      const orderId = historyItem.order?.externalOrderId;
      ensureConfirmationOfferWindowStart(orderId);

      await this.callLambda();
      this.updateOfferWindowExpiryState(orderId);

      const previousPaymentType = this.getLastHistoryItem()?.order?.paymentType;
      if (previousPaymentType) {
        useDraftStore().selectPaymentMethod(previousPaymentType);
      }
    },
    async initializePreview() {
      this.isLoadingExtrasell = true;
      this.isInitializingPreview = true;
      this.syncExtrasellPanel();
      let previewProductDetails = window.productDetails;

      try {
        const domainId = window?.globalConfig?.domainId;
        if (!domainId) {
          console.warn("[Preview] No domainId, cannot preview");
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const rawStep = parseInt(params.get("step") || "0", 10);
        const targetStep =
          Number.isFinite(rawStep) && rawStep >= 0 ? rawStep : 0;
        const parentChoiceId = params.get("parentChoiceId");
        const productId = window?.productDetails?.id;
        if (!productId) {
          console.warn("[Preview] No productId, cannot preview");
          return;
        }

        const electedInitialChoices = electPreviewInitialChoices({
          productDetails: window.productDetails,
          productId,
          parentChoiceId,
          electItemOfGroup: AbTests.electItemOfGroup,
        });
        if (electedInitialChoices.length === 0) {
          console.warn("[Preview] No initialChoices, cannot preview");
          return;
        }

        // FORGE-308: prefer a real paymentType from localStorage when present.
        const realPaymentType =
          useOrderHistoryStore().lastOrderHistoryItem?.order?.paymentType ||
          "sepa";

        this.seedPreviewOrderHistory(
          electedInitialChoices,
          productId,
          realPaymentType,
        );
        markVanguardOnlyPreviewTab();

        const url = `${process.env.VUE_APP_ORION_API_BASE_URL}/extrasell`;
        const payload =
          targetStep > 0
            ? buildCrossSellPreviewRequest(
                domainId,
                electedInitialChoices,
                targetStep,
              )
            : buildUpsellPreviewRequest(domainId, electedInitialChoices);

        const data = await fetchExtrasellWithStepAdvance(
          (extrasellUrl, extrasellPayload) =>
            this.doCall(extrasellUrl, extrasellPayload),
          url,
          payload,
        );

        if (data?.stepType) {
          if (data.productDetails) {
            previewProductDetails = data.productDetails;
          }
          this.step = data.step;
          this.stepType = data.stepType;
          useOrderHistoryStore().setCurrentStepType(data.stepType);

          if (this.stepType === "crossSells") {
            // FORGE-443 — defensive: enforce post-purchase title in preview path too
            setPostPurchaseTitle();
            this.products = this.findCrossSellsChoices(data.productDetails);
            this.showprocessorder = true;
            this.showaddtocart = false;
          } else if (this.stepType === "postUpsells") {
            // FORGE-443 — defensive: enforce post-purchase title in preview path too
            setPostPurchaseTitle();
            this.products = this.findUpsellChoices(data.productDetails);
            this.showprocessorder = true;
            this.showaddtocart = false;
          } else {
            console.warn(
              "[Preview] Unexpected stepType:",
              this.stepType,
              "— lambda may have no offers configured for this step",
            );
          }
        } else {
          console.warn(
            "[Preview] Lambda returned no offer — thank-you preview",
          );
          this.products = null;
          this.stepType = null;
        }
      } catch (error) {
        console.error("[Preview] Lambda call failed:", error);
        this.previewDone = false;
      } finally {
        this.isInitializingPreview = false;
        this.isLoadingExtrasell = false;
        this.hasExtrasellOffers =
          Array.isArray(this.products) && this.products.length > 0;
        this.syncExtrasellPanel();
        this.publishConfirmationContext(previewProductDetails);
      }
    },
    async buildCart() {
      for (const oldCartItem of this.retryItem.cart.items) {
        const newCartItem = window.productDetails;

        const choiceId = oldCartItem.choice.id;
        newCartItem.choice = newCartItem.choices.find(
          (choice) => choice.id === choiceId,
        );
        newCartItem.qtyIncr = 1;
        newCartItem.qty = oldCartItem.qty;

        CartService.addToCart(newCartItem);
      }
    },
    buildNewRetryOrder() {
      this.newRetryOrder = Object.assign({}, this.retryItem.order);
      // keep the previous orderId from the original element
      if (!this.newRetryOrderId) {
        this.newRetryOrderId = Utils.generateUID();
      }
      this.newRetryOrder.externalOrderId = this.newRetryOrderId;
      this.newRetryOrder.status = "pending";
      this.newRetryOrder.isPaymentCaptured = false;
      this.newRetryOrder.isRetry = true;

      useDraftStore().updateOrder(this.newRetryOrder);
    },
    async sendRetryDraftOrder() {
      useDraftStore().sendingDraftOrder(true);

      const preparedOrder = Orion.prepareOrder(
        useCartStore().cart,
        this.newRetryOrder,
      );
      useDraftStore().updateOrder(preparedOrder);

      useDraftStore().sendingDraftOrder(false);

      const minifiedCart = CartService.minifyCart();

      await Orion.sendOrder(
        minifiedCart,
        preparedOrder,
        window.globalConfig.domainId,
      );

      useOrderHistoryStore().removeRetryOrders();
      useOrderHistoryStore().addOrderToHistory(
        useCartStore().cart,
        preparedOrder,
        this.step,
      );

      const currentParamPlusData = Redirect.getParamPlusData();
      if (currentParamPlusData?.orderId !== preparedOrder.externalOrderId) {
        PaymentMonitoring.captureMessage(
          "extrasell_retry_param_plus_not_updated",
          {
            level: "error",
            tags: {
              step: FLOW_STEPS.RETRY_CIT,
              bug_id: BUG_IDS.PARAM_PLUS_STALE,
              payment_type: preparedOrder.paymentType || "unknown",
              reason: "param_plus_orderId_differs_from_prepared_order",
            },
            context: {
              retryOrderId: this.retryOrderId,
              preparedOrderId: preparedOrder.externalOrderId,
              paramPlusOrderId: currentParamPlusData?.orderId || null,
              retryUrl: currentParamPlusData?.retryUrl || null,
              explanation:
                "sendRetryDraftOrder() sent orderId to Orion but localStorage PARAM_PLUS still has the old orderId. The waiting page will poll the wrong orderId → NOTFOUND → infinite retry loop.",
            },
            fingerprint: ["extrasell-retry-param-plus-not-updated"],
          },
        );
      }

      useDraftStore().draftOrderSent(true);
    },
    async callLambda() {
      if (!this.showRetry) {
        this.isLoadingExtrasell = true;
        this.syncExtrasellPanel();
      }

      let fingerprint = null;
      try {
        const payload = this.buildPayload();
        fingerprint = this.buildRequestFingerprint(payload);

        // Order Page UX: try the prefetch cache first (populated by waiting/Payment.vue).
        // Hit -> instant render. The fingerprint for this state is recorded
        // even on a cache hit, so a later trigger for the same state
        // (vue-app-mounted, watcher) no-ops once the single-use cache has
        // already been consumed, instead of falling through to a real
        // duplicate POST.
        const staleHistory = this.isPreviewMode()
          ? this.getPreviewScopedHistory()
          : useOrderHistoryStore().orderHistory;
        const cachedData = this.readPrefetchCache();
        if (
          cachedData &&
          !isStalePostUpsellPayload(
            cachedData,
            this.getLastHistoryItem(),
            staleHistory,
            {
              previewConsumed: isPreviewExtrasellConsumed(
                window?.globalConfig?.domainId,
              ),
            },
          ) &&
          postUpsellAppliesToPayload(cachedData, payload)
        ) {
          this.applyExtrasellData(cachedData);
          this.lastAppliedFingerprint = fingerprint;
          return;
        }

        if (!payload.history || payload.history.length === 0) {
          return;
        }

        // Same state already applied (mounted + vue-app-mounted + watcher
        // racing without an in-flight request to share): no-op. A user
        // action (skip, new order) mutates the history first, which changes
        // the fingerprint and lets a new call through.
        if (fingerprint === this.lastAppliedFingerprint) {
          return;
        }

        // A request for this exact state is already in flight: reuse it
        // instead of firing a duplicate POST.
        if (
          this.callLambdaPromise &&
          fingerprint === this.callLambdaFingerprint
        ) {
          await this.callLambdaPromise;
          return;
        }

        const url = `${process.env.VUE_APP_ORION_API_BASE_URL}/extrasell`;
        const requestPromise = fetchExtrasellWithStepAdvance(
          (extrasellUrl, extrasellPayload) =>
            this.doCall(extrasellUrl, extrasellPayload),
          url,
          payload,
        );
        this.callLambdaFingerprint = fingerprint;
        this.callLambdaPromise = requestPromise;
        requestPromise.finally(() => {
          // Only the latest in-flight request may clear the shared refs.
          // Otherwise a stale response finishing after a newer skip/fetch
          // would wipe the active promise and break further dedup.
          if (this.callLambdaPromise === requestPromise) {
            this.callLambdaPromise = null;
            this.callLambdaFingerprint = null;
          }
        });

        const data = await requestPromise;
        // Drop obsolete responses: a newer user action (skip, new order)
        // already moved the confirmation state past this fingerprint.
        const currentFingerprint = this.buildRequestFingerprint(
          this.buildPayload(),
        );
        if (fingerprint !== currentFingerprint) {
          return;
        }

        if (data) {
          this.applyExtrasellData(data);
          this.lastAppliedFingerprint = fingerprint;
        } else {
          this.products = null;
          this.hasExtrasellOffers = false;
          this.publishConfirmationContext(window.productDetails);
        }
      } finally {
        if (!this.showRetry) {
          // A stale caller must not hide the loader while a newer fetch
          // for a different fingerprint is still running.
          if (
            this.callLambdaPromise == null ||
            this.callLambdaFingerprint === fingerprint
          ) {
            this.isLoadingExtrasell = false;
            this.syncExtrasellPanel();
          }
        }
      }
    },
    buildRequestFingerprint(payload) {
      return JSON.stringify({
        domainId: payload.domainId,
        currentStep: payload.currentStep,
        previousStepType: payload.previousStepType,
        history: payload.history,
      });
    },
    applyExtrasellData(data) {
      let extrasellData = data;
      if (
        isStalePostUpsellPayload(
          data,
          this.getLastHistoryItem(),
          this.isPreviewMode()
            ? this.getPreviewScopedHistory()
            : useOrderHistoryStore().orderHistory,
          {
            previewConsumed: isPreviewExtrasellConsumed(
              window?.globalConfig?.domainId,
            ),
          },
        )
      ) {
        extrasellData =
          data.productDetails?.crossSells?.length > 0
            ? { ...data, stepType: "crossSells" }
            : { ...data, stepType: null };
      }

      this.step = extrasellData.step;
      this.stepType = extrasellData.stepType;
      if (extrasellData.stepType) {
        useOrderHistoryStore().setCurrentStepType(extrasellData.stepType);
      }

      if (this.stepType === "crossSells") {
        // FORGE-443 — defensive: enforce post-purchase title on cross-sell view
        setPostPurchaseTitle();
        this.products = this.findCrossSellsChoices(
          extrasellData.productDetails,
        );
        this.showprocessorder = true;
        this.showaddtocart = false;
      } else if (this.stepType === "postUpsells") {
        // FORGE-443 — defensive: enforce post-purchase title on upsell view
        setPostPurchaseTitle();
        this.products = this.findUpsellChoices(extrasellData.productDetails);
        if (
          !this.products.length &&
          this.getLastHistoryItem()?.action !== "skip"
        ) {
          this.skipProcessAction();
          return;
        }
        this.showprocessorder = true;
        this.showaddtocart = false;
      } else {
        this.products = null;
      }

      this.hasExtrasellOffers =
        Array.isArray(this.products) && this.products.length > 0;
      this.publishConfirmationContext(extrasellData.productDetails);
    },
    publishConfirmationContext(productDetails) {
      useConfirmationContextStore().setContext({
        hasExtrasellOffers: this.hasExtrasellOffers,
        stepType: this.stepType,
        step: this.step,
        parentChoiceId: this.resolveActiveParentChoiceId(productDetails),
        orderPageItemId: this.resolveActiveOrderPageItemId(productDetails),
      });
    },
    resolveActiveUpsellGroup(productDetails) {
      if (
        this.stepType !== "postUpsells" ||
        !productDetails?.postUpsells?.length
      ) {
        return null;
      }

      const lastHistoryItem = this.getLastHistoryItem();
      if (!lastHistoryItem) {
        return productDetails.postUpsells[0] || null;
      }

      const cartItems = lastHistoryItem.cart?.items;
      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        return productDetails.postUpsells[0] || null;
      }

      const oldChoiceIds = cartItems
        .map((cartItem) => cartItem?.choice?.id)
        .filter(Boolean);
      return (
        productDetails.postUpsells.find((group) =>
          oldChoiceIds.includes(group.parentId),
        ) || null
      );
    },
    resolveActiveParentChoiceId(productDetails) {
      return this.resolveActiveUpsellGroup(productDetails)?.parentId || null;
    },
    resolveActiveOrderPageItemId(productDetails) {
      if (this.stepType === "crossSells") {
        const crossSells =
          productDetails?.confirmationSettings?.crossSells || {};
        const stepKey = String(this.step ?? "");
        const match = Object.entries(crossSells).find(
          ([, value]) => String(value?.step) === stepKey,
        );
        if (match) {
          return match[0];
        }
        if (crossSells[stepKey]?.orderPageItemId) {
          return crossSells[stepKey].orderPageItemId;
        }
        return crossSells[stepKey] ? stepKey : null;
      }

      const upsellGroup = this.resolveActiveUpsellGroup(productDetails);
      if (!upsellGroup?.children?.length) {
        return null;
      }

      return (
        normalizePostUpsellChoiceGroup(upsellGroup.children[0])
          .orderPageItemId || null
      );
    },
    readPrefetchCache() {
      // Cache lives only for Order Pages (prefetch path). 5min TTL, single-use.
      if (!window?.globalConfig?.isOrderPage) {
        return null;
      }
      const orderId = Redirect.getParamPlusData()?.orderId;
      if (!orderId) {
        return null;
      }
      const cacheKey = `extrasell_${orderId}`;
      try {
        const raw = sessionStorage.getItem(cacheKey);
        if (!raw) {
          return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) {
          sessionStorage.removeItem(cacheKey);
          return null;
        }
        // Single-use: clear after consumption to avoid replay on re-mount.
        sessionStorage.removeItem(cacheKey);
        return parsed.data || null;
      } catch (_) {
        return null;
      }
    },

    buildPayload() {
      const historyFromStorage = this.isPreviewMode()
        ? this.getPreviewScopedHistory()
        : useOrderHistoryStore().currentPageOrderHistory;

      const payload = buildExtrasellPayload({
        historyFromStorage,
        domainId: window.globalConfig.domainId,
        fallbackStepType: this.stepType,
      });

      if (payload.history.length > 0) {
        this.step = payload.currentStep;
      }

      return payload;
    },
    doCall(url, payload) {
      return new Promise((resolve, reject) => {
        axios.post(url, payload).then(
          (response) => {
            resolve(response);
          },
          (error) => {
            console.error(error);
            reject(error);
          },
        );
      });
    },
    findUpsellChoices(productDetails) {
      const history = this.isPreviewMode()
        ? this.getPreviewScopedHistory()
        : useOrderHistoryStore().currentPageOrderHistory;
      const oldChoiceIds =
        resolveUpsellParentChoiceIds(history).filter(Boolean);
      if (oldChoiceIds.length === 0) {
        return [];
      }

      const upsellGroups = productDetails["postUpsells"].filter((choice) =>
        oldChoiceIds.includes(choice.parentId),
      );

      const electedUpsells = [];
      for (const upsellGroup of upsellGroups) {
        for (const childGroup of upsellGroup.children) {
          const { choices: upsellChoiceGroup } =
            normalizePostUpsellChoiceGroup(childGroup);
          const electedItem = AbTests.electItemOfGroup(upsellChoiceGroup);
          const electedChoice = productDetails.choices.find(
            (choice) => choice.id === electedItem.id,
          );
          electedChoice.argument1 = electedItem.argument1;
          electedChoice.argument2 = electedItem.argument2;
          electedChoice.argument3 = electedItem.argument3;
          electedUpsells.push(electedChoice);
        }
      }

      const products = [];
      for (const electedUpsell of electedUpsells) {
        const clonedProduct = JSON.parse(JSON.stringify(productDetails));
        electedUpsell.default = true; // mark as default
        clonedProduct.choices = [electedUpsell]; // override choices with only elected ones
        // Make each post-upsell product unique by appending choice ID to product ID
        clonedProduct.id = `${clonedProduct.id}_upsell_${electedUpsell.id}`;
        products.push(clonedProduct);
      }

      return products;
    },
    findCrossSellsChoices(productDetails) {
      // Step is already used by lambda to filter crossSells
      const crossSellsGroups = productDetails["crossSells"] || [];

      const electedCrossSells = [];
      for (const crossSellGroup of crossSellsGroups) {
        const electedItem = AbTests.electItemOfGroup(crossSellGroup);
        const electedChoice = productDetails.choices.find(
          (choice) => choice.id === electedItem.id,
        );
        if (!electedChoice) {
          continue;
        }
        electedChoice.argument1 = electedItem.argument1;
        electedChoice.argument2 = electedItem.argument2;
        electedChoice.argument3 = electedItem.argument3;
        electedCrossSells.push(electedChoice);
      }

      const electedCrossSell = electedCrossSells[0];
      if (!electedCrossSell) {
        return [];
      }

      const clonedProduct = JSON.parse(JSON.stringify(productDetails));
      electedCrossSell.default = true; // mark as default
      clonedProduct.choices = [electedCrossSell]; // override choices with only elected ones
      return [clonedProduct];
    },

    async processUpsellOrder() {
      this.processingOrder = true;

      try {
        // Create a new order for the upsell
        const upsellOrder = this.createUpsellOrder();

        // Prepare the order with Orion
        const preparedOrder = Orion.prepareOrder(
          useCartStore().cart,
          upsellOrder,
        );

        // Send the order to Orion
        const minifiedCart = CartService.minifyCart();
        await Orion.sendOrder(
          minifiedCart,
          preparedOrder,
          window.globalConfig.domainId,
        );

        // Add the order to history
        useOrderHistoryStore().addOrderToHistory(
          useCartStore().cart,
          preparedOrder,
          this.step,
        );

        // Clear the cart
        CartService.emptyCart();

        // Move to next step
        this.skipProcessAction();
      } catch (error) {
        console.error("Error processing upsell order:", error);
        // Handle error - could show a toast notification
      } finally {
        this.processingOrder = false;
      }
    },

    createUpsellOrder() {
      // Create a new order based on the previous order but as an upsell
      const lastOrder = this.getLastHistoryItem()?.order;

      return {
        externalOrderId: Utils.generateUID(),
        orderType: "upsell",
        status: "pending",
        isPaymentCaptured: false,
        paymentType: lastOrder?.paymentType || "bank_card",
        // Copy billing info from last order
        billingAddress: lastOrder?.billingAddress,
        shippingAddress: lastOrder?.shippingAddress,
        // Add other necessary order properties
      };
    },

    skipProcessAction() {
      CartService.addToCartLoading(true);

      this.retryOrderId = null;
      this.showRetry = false;
      markPreviewExtrasellConsumed(window?.globalConfig?.domainId);
      useOrderHistoryStore().addStepToHistory(this.step);

      CartService.addToCartLoading(false);
    },
  },
  beforeUnmount() {
    document.removeEventListener("vue-app-mounted", this.onVueAppMounted);
    this.clearOfferWindowExpiryTimer();
  },
};
</script>
